import { get, post, put } from 'helpers/api';
import debounce from 'lodash/debounce';

const rootPath = '/contacts';

export function searchContacts({ databases, fullSerialization, term, type, projectId }, onSuccess, onFailure) {
  const params = { fullSerialization, term, type };

  if (projectId) {
    params.projectId = projectId;
  } else if (databases) {
    params.databaseIds = databases.map(database => database.id);
  } else {
    params.allDatabases = true;
  }

  get(`${rootPath}/search.json`, { params })
    .then(response => onSuccess(response.contacts || []), onFailure);
}

export const debouncedSearchContacts = debounce((options, onSuccess, onFailure) => {
  searchContacts(options, onSuccess, onFailure);
}, 300);

// `searchContacts` above only reads the legacy contact databases (contacts_people /
// contacts_companies owned by a Database). CRM contacts live in backoffice_contacts, so CRM
// accounts have to search here instead: Backoffice::Contacts::Search returns those *plus* the
// legacy database contacts, deduped, making it a superset of `searchContacts` for those accounts.
const CRM_CONTACT_TYPES = {
  'Contacts::Person': 'person',
  'Contacts::Company': 'business'
};

export function searchCrmContacts({ term, type }, onSuccess, onFailure) {
  get('/backoffice/contacts/search', { params: { query: term, type: CRM_CONTACT_TYPES[type] } })
    .then(contacts => onSuccess(contacts || []), onFailure);
}

export const debouncedSearchCrmContacts = debounce((options, onSuccess, onFailure) => {
  searchCrmContacts(options, onSuccess, onFailure);
}, 300);

export function save(contact) {
  const saveMethod = contact.contactType === 'Contacts::Company' ? saveCompany : savePerson;
  return saveMethod(contact);
}

export function searchCallLists(searchTerm, onSuccess) {
  get(`${rootPath}/call_lists.json`, { params: { searchTerm } })
    .then(response => onSuccess(response.callLists));
}

export const saveRelatedContactsQuery = params => (
  post(`${rootPath}/related_contacts.json`, params)
    .then(({ relatedContactSearch }) => relatedContactSearch)
);

function saveCompany(company) {
  const params = { ...company };
  params.name = params.name || '';

  delete params.contactType;
  prepareAddressParams(company, params);
  prepareContactTypesParams(company, params);
  prepareDatabaseParams(company, params);
  prepareSocialMediaParams(company, params);

  delete params.phoneNumber;
  delete params.phoneNumbers;
  if (company.phoneNumbers) {
    params.phoneNumbersAttributes = company.phoneNumbers;
  } else {
    params.phoneNumber = company.phoneNumber;
  }

  let request;
  const body = { contactsCompany: params };

  if (params.id) {
    request = put(`/contacts/companies/${params.id}`, body);
  } else {
    request = post('/contacts/companies.json', body);
  }

  return request.then(response => response.company);
}

function savePerson(person) {
  const params = { ...person };
  params.firstName = params.firstName || '';
  params.lastName = params.lastName || '';

  // NOTE: This is currently used for two flows, one of which will never have person.company, so
  //       an else would be incorrect and could incorrectly clear the company in one of the flows.
  if (person.company) {
    params.contactsCompanyId = person.company.id || null;
    delete params.company;
  }

  delete params.contactType;
  delete params.name;
  delete params['name?'];
  delete params['address?'];
  prepareAddressParams(person, params);
  prepareContactTypesParams(person, params);
  prepareDatabaseParams(person, params);
  prepareSocialMediaParams(person, params);

  delete params.email;
  delete params.emailAddresses;
  if (person.emailAddresses) {
    params.emailAddressesAttributes = person.emailAddresses;
  } else {
    params.email = person.email;
  }

  delete params.mobilePhoneNumber;
  delete params.phoneNumbers;
  if (person.phoneNumbers) {
    params.phoneNumbersAttributes = person.phoneNumbers;
  } else {
    params.mobilePhoneNumber = person.mobilePhoneNumber;
  }

  let request;
  const body = { contactsPerson: params };

  if (params.id) {
    request = put(`/contacts/people/${params.id}`, body);
  } else {
    request = post('/contacts/people.json', body);
  }

  return request.then(response => response.person);
}

function prepareAddressParams(contact, params) {
  delete params.addresses;
  delete params.addressOne;
  delete params.addressTwo;
  delete params.city;
  delete params.state;
  delete params.zip;

  if (contact.addresses) {
    params.addressesAttributes = contact.addresses;
  } else {
    const { addressOne, addressTwo, city, state, zip } = contact;
    params.addressAttributes = { addressOne, addressTwo, city, state, zip };
  }
}

function prepareContactTypesParams(contact, params) {
  delete params.contactTypes;
  params.contactTypesAttributes = contact.contactTypes;
}

function prepareDatabaseParams(contact, params) {
  delete params.database;
  if (contact.database) {
    params.databaseId = contact.database.id;
  }
}

function prepareSocialMediaParams(contact, params) {
  delete params.socialMediaData;
  delete params.socialMediaOptions;

  for (const option of contact.socialMediaOptions) {
    const socialMediaDatum = contact.socialMediaData.find(datum => datum.option === option);
    params[option.value] = socialMediaDatum ? socialMediaDatum.value : null;
  }
}
